/**
 * IXVerify v2 — the notification bell (ixv2 WP-3.4; plan §3.8, §10 shared/).
 *
 * Lives in the module AppBar's right slot (on-gradient styling, 44px touch
 * target collapsing at sm: — R7). Unread badge + dropdown list over
 * `GET /api/ixverify/notifications`; live over the SSE
 * `ixverify:{userId}` channel's 'ixverify_notification' frames
 * (subscribeIxverifyNotificationsBell — supabase mode in local dev listens to
 * the legacy postgres_changes table and simply hears nothing for v2 rows;
 * the bell still works via fetch-on-open).
 *
 * Clicking a row marks it read and navigates its deep link (the client mirror
 * of the backend map). Rows are PHI-free by construction (R3) — nothing here
 * renders subject data.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Bell, CheckCheck, FileCheck, Inbox, Loader2, UserCheck } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { subscribeIxverifyNotificationsBell } from '../../../lib/realtimeTransport';
import * as ixv from '../../lib/api';

const LIST_LIMIT = 15;

function typeIcon(type: string) {
  switch (type) {
    case 'mismatch_recorded':
    case 'report_delivery_failed':
    case 'drift_alert':
      return AlertTriangle;
    case 'second_check_requested':
    case 'second_check_approved':
    case 'second_check_rejected':
      return UserCheck;
    case 'report_sent':
      return FileCheck;
    default:
      return Bell;
  }
}

function isAlert(type: string): boolean {
  return type === 'mismatch_recorded' || type === 'report_delivery_failed' || type === 'drift_alert' || type === 'second_check_rejected';
}

/** Compact relative timestamp (meta text — no live ticking needed). */
export function timeAgo(iso: string, now: Date = new Date()): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const s = Math.max(0, Math.floor((now.getTime() - then) / 1000));
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState<ixv.IxvNotification[] | null>(null);
  const [loading, setLoading] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await ixv.listNotifications({ limit: LIST_LIMIT });
      setItems(result.notifications);
      setUnread(result.unread);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Badge on mount (cheap head count); the list loads when the panel opens.
  useEffect(() => {
    if (!user?.id) return;
    ixv.getUnreadCount().then(setUnread).catch(() => {});
  }, [user?.id]);

  // Live bell over SSE (event-name-filtered on the shared channel).
  useEffect(() => {
    if (!user?.id) return undefined;
    return subscribeIxverifyNotificationsBell<ixv.IxvNotification>(
      user.id,
      (row) => {
        setUnread((n) => n + 1);
        setItems((list) => (list ? [row, ...list].slice(0, LIST_LIMIT) : list));
      },
      (row) => {
        setItems((list) => (list ? list.map((n) => (n.id === row.id ? row : n)) : list));
        if (row.read) setUnread((n) => Math.max(0, n - 1));
      }
    );
  }, [user?.id]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = useCallback(() => {
    setOpen((was) => {
      const next = !was;
      if (next) void load();
      return next;
    });
  }, [load]);

  const openNotification = useCallback(
    async (n: ixv.IxvNotification) => {
      setOpen(false);
      if (!n.read) {
        setItems((list) => (list ? list.map((x) => (x.id === n.id ? { ...x, read: true } : x)) : list));
        setUnread((c) => Math.max(0, c - 1));
        ixv.markNotificationRead(n.id).catch(() => {});
      }
      navigate(ixv.deepLinkFor(n));
    },
    [navigate]
  );

  const markAll = useCallback(async () => {
    setItems((list) => (list ? list.map((n) => ({ ...n, read: true })) : list));
    setUnread(0);
    try {
      await ixv.markAllNotificationsRead();
    } catch {
      /* the next open refetches the truth */
    }
  }, []);

  if (!user?.id) return null;

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={toggle}
        aria-label={unread > 0 ? `Notifications (${unread} unread)` : 'Notifications'}
        aria-haspopup="true"
        aria-expanded={open}
        data-testid="notification-bell"
        className="relative inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg text-white/90 transition-colors duration-150 hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/60 sm:min-h-0 sm:min-w-0 sm:h-9 sm:w-9"
      >
        <Bell className="h-5 w-5" aria-hidden="true" />
        {unread > 0 && (
          <span
            data-testid="bell-badge"
            className="absolute right-0.5 top-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold leading-none text-white sm:-right-1 sm:-top-1"
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Notifications"
          className="absolute right-0 z-40 mt-2 w-80 max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800"
        >
          <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-3 py-2 dark:border-gray-700">
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">Notifications</span>
            <button
              type="button"
              onClick={() => void markAll()}
              disabled={unread === 0}
              data-testid="mark-all-read"
              className="inline-flex min-h-[44px] items-center gap-1 rounded-lg px-2 text-xs font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 dark:text-primary-400 dark:hover:bg-primary-900/20 sm:min-h-0 sm:py-1"
            >
              <CheckCheck className="h-3.5 w-3.5" aria-hidden="true" /> Mark all read
            </button>
          </div>

          {loading && items == null ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-primary-600 motion-reduce:animate-none dark:text-primary-400" aria-hidden="true" />
            </div>
          ) : !items || items.length === 0 ? (
            <div className="flex flex-col items-center justify-center px-4 py-8 text-center">
              <Inbox className="mb-2 h-8 w-8 text-gray-300 dark:text-gray-600" aria-hidden="true" />
              <p className="text-sm text-gray-500 dark:text-gray-400">You're all caught up</p>
            </div>
          ) : (
            <ul className="max-h-[min(24rem,60vh)] divide-y divide-gray-100 overflow-y-auto dark:divide-gray-700">
              {items.map((n) => {
                const Icon = typeIcon(n.type);
                return (
                  <li key={n.id}>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => void openNotification(n)}
                      className={`flex w-full items-start gap-2.5 px-3 py-2.5 text-left transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 dark:hover:bg-gray-700/50 ${
                        n.read ? '' : 'bg-primary-50/60 dark:bg-primary-900/10'
                      }`}
                    >
                      <Icon
                        className={`mt-0.5 h-4 w-4 flex-shrink-0 ${
                          isAlert(n.type)
                            ? 'text-amber-600 dark:text-amber-400'
                            : 'text-primary-600 dark:text-primary-400'
                        }`}
                        aria-hidden="true"
                      />
                      <span className="min-w-0 flex-1">
                        <span className={`block truncate text-sm ${n.read ? 'font-normal text-gray-700 dark:text-gray-300' : 'font-medium text-gray-900 dark:text-gray-100'}`}>
                          {n.title}
                        </span>
                        {n.message && (
                          <span className="mt-0.5 block text-xs leading-snug text-gray-500 line-clamp-2 dark:text-gray-400">{n.message}</span>
                        )}
                        <span className="mt-0.5 block text-[10px] uppercase tracking-widest text-gray-400 dark:text-gray-500">
                          {timeAgo(n.created_at)}
                          {!n.read && <span className="sr-only"> — unread</span>}
                        </span>
                      </span>
                      {!n.read && (
                        <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary-600 dark:bg-primary-400" aria-hidden="true" />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
